import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  OAuthProvider,
} from 'firebase/auth';
import * as Sentry from '@sentry/react';
import { auth } from '../config/firebase';
import { api } from '@viilutalous/common';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user permissions from the backend to get is_admin flag
  const fetchUserPermissions = async () => {
    try {
      const response = await api.get('/user/permissions');
      return response.data;
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return null;
    }
  };

  // Log session start to backend for access logging
  const logSessionStart = async (authMethod = 'firebase') => {
    try {
      await api.post('/auth/session', { auth_method: authMethod });
    } catch (error) {
      // Don't fail login if session logging fails
      console.error('Error logging session start:', error);
    }
  };

  // Log session end to backend for access logging
  const logSessionEnd = async () => {
    try {
      await api.delete('/auth/session');
    } catch (error) {
      // Don't fail logout if session logging fails
      console.error('Error logging session end:', error);
    }
  };

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        // Get the ID token and store it
        const token = await firebaseUser.getIdToken();
        localStorage.setItem('access_token', token);

        // Fetch user permissions to get is_admin flag
        const permissions = await fetchUserPermissions();

        setUser({
          uid: firebaseUser.uid,
          user_id: permissions?.user_id || null,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          is_admin: permissions?.is_admin || false,
        });
        // Attach identity to every Sentry event and Session Replay from here on.
        Sentry.setUser({
          id: permissions?.user_id || firebaseUser.uid,
          email: firebaseUser.email || undefined,
          username: firebaseUser.displayName || undefined,
        });
      } else {
        // User is signed out
        localStorage.removeItem('access_token');
        setUser(null);
        Sentry.setUser(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Refresh token periodically (Firebase tokens expire after 1 hour)
  useEffect(() => {
    if (!user) return;

    const refreshToken = async () => {
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        const token = await firebaseUser.getIdToken(true); // Force refresh
        localStorage.setItem('access_token', token);
      }
    };

    // Refresh token every 55 minutes (before 1 hour expiration)
    const interval = setInterval(refreshToken, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('access_token', token);
      await logSessionStart('firebase_email');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: getFirebaseErrorMessage(error)
      };
    }
  };

  const register = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('access_token', token);
      await logSessionStart('firebase_register');
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: getFirebaseErrorMessage(error)
      };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('access_token', token);
      await logSessionStart('firebase_google');
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      return {
        success: false,
        error: getFirebaseErrorMessage(error)
      };
    }
  };

  const loginWithMicrosoft = async () => {
    try {
      const provider = new OAuthProvider('microsoft.com');
      const userCredential = await signInWithPopup(auth, provider);
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('access_token', token);
      await logSessionStart('firebase_microsoft');
      return { success: true };
    } catch (error) {
      console.error('Microsoft login error:', error);
      return {
        success: false,
        error: getFirebaseErrorMessage(error)
      };
    }
  };

  const logout = async () => {
    try {
      await logSessionEnd();
      await signOut(auth);
      localStorage.removeItem('access_token');
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return {
        success: false,
        error: getFirebaseErrorMessage(error)
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    loginWithMicrosoft,
    logout,
    resetPassword,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Helper function to convert Firebase errors to user-friendly messages
function getFirebaseErrorMessage(error) {
  switch (error.code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Invalid email or password';
    case 'auth/email-already-in-use':
      return 'Email already in use';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters';
    case 'auth/invalid-email':
      return 'Invalid email address';
    case 'auth/operation-not-allowed':
      return 'Operation not allowed. Please contact support.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed before completing';
    case 'auth/cancelled-popup-request':
      return 'Another popup is already open';
    default:
      return error.message || 'An error occurred during authentication';
  }
}
